import { useProductFilters } from "@nosto/search-js/preact/hooks"
import Icon from "@/elements/Icon/Icon"
import { cl } from "@nosto/search-js/utils"
import styles from "./SelectedFilters.module.css"
import { humanizeFacetValue, parseColorValue } from "@/utils/facetHelpers"

function looksLikeColorValue(raw: string | undefined): boolean {
  if (typeof raw !== "string") return false
  return raw.includes("|#") || raw.includes("|http")
}

function looksLikeRangeValue(raw: string | undefined): boolean {
  if (typeof raw !== "string") return false
  return /^\[[^\]]* TO [^\]]*\]$/.test(raw) || /\d+ - \d+/.test(raw)
}

function formatRange(raw: string, isPrice: boolean): string {
  const trimmed = raw.replace(/^\[/, "").replace(/\]$/, "")
  const [start, end] = trimmed.split(/ TO | - /)
  if (!isPrice) return `${start} - ${end}`
  const locale =
    typeof window !== "undefined" && window.Shopify?.locale && window.Shopify?.country
      ? `${window.Shopify.locale}-${window.Shopify.country}`
      : "en-US"
  const currency = window.Shopify?.currency?.active ?? "USD"
  const formatter = new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 0 })
  const parsedStart = Number(start)
  const parsedEnd = Number(end)
  const startStr = Number.isFinite(parsedStart) ? formatter.format(parsedStart) : start
  const endStr = Number.isFinite(parsedEnd) ? formatter.format(parsedEnd) : end
  return `${startStr} - ${endStr}`
}

type Filter = ReturnType<typeof useProductFilters>["filters"][number]

type FilterGroup = {
  key: string
  representative: NonNullable<Filter>
  filters: NonNullable<Filter>[]
}

function isColorFilter(facetName: string, rawValue: string): boolean {
  return facetName.includes("color") || rawValue.includes("|#") || rawValue.includes("|http")
}

/**
 * Group filters by normalized `name:value` so case-variant entries (e.g.
 * "Cotton" + "cotton") collapse to a single pill. `TermsFacet` applies one
 * Nosto filter per merged variant to preserve query parity, which would
 * otherwise surface as duplicated pills here. Removing the merged
 * pill removes every underlying filter so the displayed and applied state
 * stay in sync.
 *
 * Color filters get a second dedupe layer keyed by the parsed display name
 * so that the swatched and bare encodings (`"Black|#000000"` + `"Black"`)
 * collapse to a single pill matching the facet panel. The swatch-bearing
 * filter wins as the representative so the pill renders the color dot.
 */
function groupFilters(filters: Filter[]): FilterGroup[] {
  const order: string[] = []
  const groups = new Map<string, FilterGroup>()

  for (const filter of filters) {
    if (!filter) continue
    const rawValue = String(filter.value ?? "")
    const facetName = (filter.name ?? "").toLowerCase()
    const normalizedValue = isColorFilter(facetName, rawValue)
      ? parseColorValue(rawValue).name.toLowerCase()
      : rawValue.toLowerCase()
    const key = `${facetName}:${normalizedValue}`
    const existing = groups.get(key)
    if (existing) {
      existing.filters.push(filter)
      const existingHasSwatch = String(existing.representative.value ?? "").includes("|")
      const incomingHasSwatch = rawValue.includes("|")
      if (incomingHasSwatch && !existingHasSwatch) {
        existing.representative = filter
      }
    } else {
      groups.set(key, { key, representative: filter, filters: [filter] })
      order.push(key)
    }
  }

  return order.map(key => groups.get(key)!)
}

export default function SelectedFilters() {
  const { filters, removeAll } = useProductFilters()

  const groups = groupFilters(filters)
  if (!groups.length) return null

  return (
    <div className={styles.wrapper}>
      <span className={styles.heading}>Applied Filters ({groups.length})</span>
      <div className={styles.chips}>
        {groups.map(group => {
          const filter = group.representative
          const rawValue = String(filter.value ?? "")
          const facetName = (filter.name ?? "").toLowerCase()
          const isPrice = facetName.includes("price")
          const isColor = looksLikeColorValue(rawValue) || facetName.includes("color")
          const isRange = looksLikeRangeValue(rawValue)

          let chipText = humanizeFacetValue(rawValue)
          let swatchNode: preact.JSX.Element | null = null

          if (isRange) {
            chipText = formatRange(rawValue, isPrice)
          } else if (isColor && rawValue.includes("|")) {
            const parsed = parseColorValue(rawValue)
            chipText = humanizeFacetValue(parsed.name)
            const needsBorder = !parsed.isImage && parsed.swatch.toLowerCase() === "#ffffff"
            if (parsed.swatch) {
              swatchNode = parsed.isImage ? (
                <span className={cl(styles.swatch, needsBorder && styles.swatchBordered)} aria-hidden="true">
                  <img src={parsed.swatch} alt="" width={12} height={12} />
                </span>
              ) : (
                <span
                  className={cl(styles.swatch, needsBorder && styles.swatchBordered)}
                  style={{ backgroundColor: parsed.swatch }}
                  aria-hidden="true"
                />
              )
            }
          }

          return (
            <button
              type="button"
              key={group.key}
              className={styles.chip}
              onClick={e => {
                e.preventDefault()
                group.filters.forEach(f => f.remove())
              }}
              aria-label={`Remove filter ${chipText}`}
            >
              {swatchNode}
              <span className={styles.chipValue}>{chipText}</span>
              <Icon name="close" className={styles.chipIcon} />
            </button>
          )
        })}
        <button
          type="button"
          className={styles.clearAll}
          onClick={e => {
            e.preventDefault()
            removeAll()
          }}
        >
          Clear All
        </button>
      </div>
    </div>
  )
}
